import { Alert, Box, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { ExpandableCopyText, PageBreadcrumbs } from "@/shared/components";
import { EntityComments } from "@/shared/components/comments";
import { PageLayout } from "@/shared/components/PageLayout";
import {
  getQueryKey,
  useCollection,
} from "@/shared/domain/collections/hooks/useCollection";

import { CollectionDetails } from "./CollectionDetails";

/**
 * Details page for a single Collection.
 *
 * The collection to detail is determined from the page's URL.
 * The URL can contain the collection's ID.
 */
export const Collection: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string | undefined }>();
  const theme = useTheme();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: collection,
    error,
    isLoading,
  } = useCollection(currentOrganization?.org_id ? collectionId : undefined);

  const breadcrumbs = [
    <Link key="list" to="/collections">
      Collections
    </Link>,
    <ExpandableCopyText
      key="collectionId"
      text={collectionId || ""}
      trimLength={15}
    />,
  ];

  let errorContent = null;
  let pageContent = null;
  if (error) {
    errorContent = (
      <Box
        sx={{
          mt: theme.spacing(2),
          mb: theme.spacing(1),
        }}
      >
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  } else if (collection) {
    pageContent = (
      <CollectionDetails
        collection={collection}
        loading={isLoading}
        onUpdate={() => {
          void queryClient.invalidateQueries({
            queryKey: getQueryKey(collectionId),
          });
        }}
      />
    );
  }

  return (
    <PageLayout
      title={`Roboto - Collection - ${collectionId}`}
      headerContentLeft={<PageBreadcrumbs>{breadcrumbs}</PageBreadcrumbs>}
    >
      {errorContent}

      {pageContent}

      <Box
        sx={{
          paddingTop: theme.spacing(3),
        }}
      >
        {!isLoading && collection && (
          <EntityComments
            entityType={"collection"}
            entityId={collection.collection_id}
          />
        )}
      </Box>
    </PageLayout>
  );
};
